import { defineStore } from 'pinia'
import axios from 'axios'
import { useGinnelsStore } from '@/stores/ginnels/ginnels'
import { useCollectionsStore } from '@/stores/collections/collections'
import { useToastsStore } from '@/stores/notifications/toasts'
import { getResponse, getError } from '@/composables/api'
import router from '@/router'

export const useGinnelStore = defineStore({
   id: 'ginnel',
   state: () => ({
      ginnel: {},
      cachedGinnels: [],
      gotoFile: null,

      updatedGinnel: null, // Stores the new name and description of the Ginnel

      newCollectionID: null, // Stores the new collection ID after a Ginnel is moved

      waiting: false,
      storeToasts: useToastsStore(),
      storeGinnels: useGinnelsStore(),
      storeCollections: useCollectionsStore(),
   }),
   actions: {
      /**
         * Function: get
         * @description Sends a get requiest to the API
         * to get a ginnel.
         *
         * @param {Number} ginnel_id
      */
      get(ginnel_id, fresh = false) {
         // Check if the ginnel exists in the cache
         if (this.cachedGinnels[ginnel_id] && ! fresh) {
             // If the ginnel is found in the cache, use it directly
             this.ginnel = this.cachedGinnels[ginnel_id];
         } else {
             // If the ginnel is not found in the cache, fetch it
             axios.get('ginnel', { params: { ginnel_id: ginnel_id } })
               .then((response) => {
                  // Update the current ginnel
                  this.ginnel = getResponse(response);
                  // Store the fresh ginnel in cache
                  this.cachedGinnels[this.ginnel?.ginnel?.ginnel_id] = this.ginnel;
               })
         }
     },
     /**
        * Function: update
        * @description Sends a put requiest to the API
        * to update a ginnel.
        *
        * @param {Object} payload
     */
      update(formData) {
         this.waiting = true

         let payload = {
            ginnel_id: formData.ginnel_id,
            name: formData.name,
            description: formData.description
         }

         // If the Ginnel is moving to a different collection
         if(payload?.to_collection_id != formData?.from_collection_id)
            payload.collection_id = formData.to_collection_id

			axios.put('ginnel', payload)
         .then(response => {
            this.waiting = false

               // Add the new name and description to the updatedGinnel object
               // Used to updated the ginnel context within the template
               this.updatedGinnel = {
                  ginnel_name: formData.name,
                  description: formData.description
               }

               this.storeGinnels.ginnels.forEach((ginnel, index) => {
                  if(ginnel.ginnel_id == formData?.ginnel_id) {

                     if(this.storeGinnels.ginnels?.[index]?.ginnel_name != formData?.name)
                        this.storeGinnels.ginnels[index].ginnel_name = formData.name

                     if(this.storeGinnels.ginnels?.[index]?.description != formData?.description)
                        this.storeGinnels.ginnels[index].description = formData.description
                  }
               })

            this.storeToasts.sendToast('The Ginnel has been updated.')

            // If the Ginnel is moving to a different Collection
            if(formData?.to_collection_id != formData?.from_collection_id) {
               // Clear Cache
               this.unsetCachedGinnels()

               this.newCollectionID = formData.to_collection_id

               // Navigate to the new collection ginnel
               router.push({ name: 'collection.ginnel', params: { collection_id: formData.to_collection_id, ginnel_id: formData?.ginnel_id } })
               this.storeGinnels.get(formData.to_collection_id)

               // Set delay to allow for two toasts at the same time
               setTimeout(() => {
                  this.storeToasts.sendToast('The Ginnel has be moved to the new Collection.', 'success')
               }, 50)
            }
			})
         .catch((response) => {
            this.waiting = false

            if(getError(response))
               this.storeToasts.sendToast(getError(response), 'danger')
            else
               this.storeToasts.sendToast(response, 'danger')
         })
      },
      /**
         * Function: delete
         * @description Sends a delete requiest to the API
         * to delete a ginnel.
         *
         * @param {Object} formData
      */
      delete(formData) {
			axios.delete('ginnel', { params: formData }).then((response) => {
            this.storeGinnels.get(formData.collection_id)
            this.storeCollections.get()

            this.storeToasts.sendToast(getResponse(response))
            router.push({ name: 'collection', params: { collection_id: formData?.collection_id } })
			}).catch((response) => {
            this.storeToasts.sendToast(getError(response), 'danger')
         })
      },
      /**
         * Function: unsetCachedGinnel
         * @type {Promise}
         * @description This function removes a ginnel from the cache.
         *
         * @param {Number} ginnel_id
      */
      unsetCachedGinnel(ginnel_id) {
         return new Promise((resolve, reject) => {
            delete this.cachedGinnels[ginnel_id]
         })
       },
       unsetCachedGinnels() {
         this.cachedGinnels = []
       },
       /**
          * Function: postRequestCleanup
          * @description This function is called after an API request.
          * It removes the ginnel from the cache and fetches it again.
          *
          * It can also send an optional toast message.
          *
          * @param {Number} ginnel_id
          * @param {String} message
       */
       postRequestCleanup(ginnel_id, message = null) {
          if(ginnel_id)
             this.unsetCachedGinnel(ginnel_id)
            .then(() => {
                this.get(ginnel_id)
             })

          if(message) this.storeToasts.sendToast(message)
       },
   }
})